import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { submissionService } from '../../services/submissionService';
import { companyService } from '../../services/companyService';
import AppImage from '../../components/AppImage';
import Button from '../../components/ui/Button';
import ProgressIndicator from './components/ProgressIndicator';
import FormNavigation from './components/FormNavigation';
import PersonalInfoStep from './components/PersonalInfoStep';
import ContactInfoStep from './components/ContactInfoStep';
import EducationStep from './components/EducationStep';
import NYSCInfoStep from './components/NYSCInfoStep';
import EmergencyContactStep from './components/EmergencyContactStep';
import DocumentUploadStep from './components/DocumentUploadStep';
import ReviewSubmitStep from './components/ReviewSubmitStep';
import SuccessModal from './components/SuccessModal';
import Icon from '../../components/AppIcon';
import { motion } from 'framer-motion';

export default function ProgressiveRegistrationForm() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [currentStep, setCurrentStep] = useState(0);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [applicationId, setApplicationId] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [companyInfo, setCompanyInfo] = useState(null);

  // FIXED: Get companyId from navigation state OR localStorage (persistent)
  const [companyId, setCompanyId] = useState(() => {
    // Try navigation state first
    const stateCompanyId = location?.state?.companyId;
    if (stateCompanyId) {
      // Save to localStorage for persistence
      localStorage.setItem('registrationCompanyId', stateCompanyId);
      return stateCompanyId;
    }
    // Fallback to localStorage if navigation state is missing
    return localStorage.getItem('registrationCompanyId') || null;
  });

  const steps = [
    { title: 'Personal Info', component: PersonalInfoStep },
    { title: 'Contact', component: ContactInfoStep },
    { title: 'Education', component: EducationStep },
    { title: 'NYSC Info', component: NYSCInfoStep },
    { title: 'Emergency', component: EmergencyContactStep },
    { title: 'Documents', component: DocumentUploadStep },
    { title: 'Review', component: ReviewSubmitStep }
  ];

  const [formData, setFormData] = useState({
    // Personal Info
    title: '',
    firstName: '',
    middleName: '',
    lastName: '',
    dateOfBirth: '',
    gender: '',
    maritalStatus: '',
    nationality: '',
    // Contact Info
    email: '',
    phone: '',
    alternativePhone: '',
    whatsappNumber: '',
    streetAddress: '',
    city: '',
    state: '',
    postalCode: '',
    // Education
    institutionName: '',
    degreeType: '',
    courseOfStudy: '',
    graduationYear: '',
    classOfDegree: '',
    cgpa: '',
    certificationName: '',
    // NYSC Info
    nyscCallUpNumber: '',
    serviceBatch: '',
    stateOfDeployment: '',
    lga: '',
    commencementDate: '',
    completionDate: '',
    ppaName: '',
    ppaAddress: '',
    ppaContactPerson: '',
    // Emergency Contact
    emergencyContactName: '',
    emergencyContactRelationship: '',
    emergencyContactPhone: '',
    emergencyContactAlternativePhone: '',
    emergencyContactEmail: '',
    emergencyContactAddress: '',
    // Document Uploads
    tertiaryGraduationCert: null,
    birthCertificate: null,
    nyscAssignmentLetter: null,
    meansOfId: null,
    passportPhoto: null,
    recentPhoto1: null,
    recentPhoto2: null
  });

  const [errors, setErrors] = useState({});

  // Load saved form data from localStorage
  useEffect(() => {
    const savedData = localStorage.getItem('registrationFormData');
    if (savedData) {
      const parsedData = JSON.parse(savedData);
      // Sanitize gender field - remove invalid 'other' value if it exists
      if (parsedData?.gender && parsedData?.gender?.toLowerCase() === 'other') {
        parsedData.gender = '';
      }
      setFormData(parsedData);
    }
    const savedStep = localStorage.getItem('registrationFormStep');
    if (savedStep) {
      setCurrentStep(parseInt(savedStep));
    }

    // ADDED: Validate that companyId exists, if not redirect to company selection
    const currentCompanyId = localStorage.getItem('registrationCompanyId');
    if (!currentCompanyId && !location?.state?.companyId) {
      // No company selected - redirect to selection page
      navigate('/company-selection-hub', {
        state: { 
          error: 'Please select a company to continue with registration' 
        }
      });
    }
  }, [navigate, location?.state?.companyId]);

  // Fetch company information
  useEffect(() => {
    const fetchCompanyInfo = async () => {
      if (companyId) {
        try {
          const company = await companyService?.getById(companyId);
          setCompanyInfo(company);
        } catch (err) {
          console.error('Error fetching company info:', err);
        }
      }
    };

    fetchCompanyInfo();
  }, [companyId]);

  // Auto-save to localStorage
  useEffect(() => {
    const autoSaveTimer = setTimeout(() => {
      localStorage.setItem('registrationFormData', JSON.stringify(formData));
      localStorage.setItem('registrationFormStep', currentStep?.toString());
    }, 1000);

    return () => clearTimeout(autoSaveTimer);
  }, [formData, currentStep]);

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateStep = (step) => {
    const newErrors = {};

    switch (step) {
      case 0: // Personal Info
        if (!formData?.title) newErrors.title = 'Title is required';
        if (!formData?.firstName) newErrors.firstName = 'First name is required';
        if (!formData?.lastName) newErrors.lastName = 'Last name is required';
        if (!formData?.dateOfBirth) newErrors.dateOfBirth = 'Date of birth is required';
        if (!formData?.gender) newErrors.gender = 'Gender is required';
        if (!formData?.maritalStatus) newErrors.maritalStatus = 'Marital status is required';
        if (!formData?.nationality) newErrors.nationality = 'Nationality is required';
        break;

      case 1: // Contact Info
        if (!formData?.email) {
          newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/?.test(formData?.email)) {
          newErrors.email = 'Email is invalid';
        }
        if (!formData?.phone) {
          newErrors.phone = 'Phone number is required';
        } else if (!/^0[789]\d{9}$/?.test(formData?.phone)) {
          newErrors.phone = 'Phone must be 11 digits starting with 07, 08, or 09';
        }
        if (!formData?.streetAddress) newErrors.streetAddress = 'Street address is required';
        if (!formData?.city) newErrors.city = 'City is required';
        if (!formData?.state) newErrors.state = 'State is required';
        break;

      case 2: // Education
        if (!formData?.institutionName) newErrors.institutionName = 'Institution name is required';
        if (!formData?.degreeType) newErrors.degreeType = 'Degree type is required';
        if (!formData?.courseOfStudy) newErrors.courseOfStudy = 'Course of study is required';
        if (!formData?.graduationYear) newErrors.graduationYear = 'Graduation year is required';
        if (!formData?.classOfDegree) newErrors.classOfDegree = 'Class of degree is required';
        if (!formData?.cgpa) newErrors.cgpa = 'CGPA is required';
        break;

      case 3: // NYSC Info
        if (!formData?.nyscCallUpNumber) newErrors.nyscCallUpNumber = 'NYSC call-up number is required';
        if (!formData?.serviceBatch) newErrors.serviceBatch = 'Service batch is required';
        if (!formData?.stateOfDeployment) newErrors.stateOfDeployment = 'State of deployment is required';
        if (!formData?.lga) newErrors.lga = 'LGA is required';
        if (!formData?.commencementDate) newErrors.commencementDate = 'Commencement date is required';
        if (!formData?.completionDate) newErrors.completionDate = 'Completion date is required';
        if (!formData?.ppaName) newErrors.ppaName = 'PPA name is required';
        if (!formData?.ppaAddress) newErrors.ppaAddress = 'PPA address is required';
        break;

      case 4: // Emergency Contact
        if (!formData?.emergencyContactName) newErrors.emergencyContactName = 'Emergency contact name is required';
        if (!formData?.emergencyContactRelationship) newErrors.emergencyContactRelationship = 'Relationship is required';
        if (!formData?.emergencyContactPhone) {
          newErrors.emergencyContactPhone = 'Emergency contact phone is required';
        } else if (!/^0[789]\d{9}$/?.test(formData?.emergencyContactPhone)) {
          newErrors.emergencyContactPhone = 'Phone must be 11 digits starting with 07, 08, or 09';
        } else if (formData?.emergencyContactPhone === formData?.phone) {
          newErrors.emergencyContactPhone = 'Emergency contact phone cannot be the same as your phone number';
        } else if (formData?.emergencyContactPhone === formData?.alternativePhone) {
          newErrors.emergencyContactPhone = 'Emergency contact phone cannot be the same as your alternative phone';
        } else if (formData?.emergencyContactPhone === formData?.whatsappNumber) {
          newErrors.emergencyContactPhone = 'Emergency contact phone cannot be the same as your WhatsApp number';
        }
        if (!formData?.emergencyContactAddress) newErrors.emergencyContactAddress = 'Emergency contact address is required';
        break;

      case 5: // Document Uploads
        if (!formData?.tertiaryGraduationCert) {
          newErrors.tertiaryGraduationCert = 'Graduation certificate is required';
        }
        if (!formData?.birthCertificate) {
          newErrors.birthCertificate = 'Birth certificate is required';
        }
        if (!formData?.nyscAssignmentLetter) {
          newErrors.nyscAssignmentLetter = 'NYSC assignment letter is required';
        }
        if (!formData?.meansOfId) {
          newErrors.meansOfId = 'Means of identification is required';
        }
        if (!formData?.passportPhoto) {
          newErrors.passportPhoto = 'Passport photograph is required';
        }
        if (!formData?.recentPhoto1) {
          newErrors.recentPhoto1 = 'First recent photograph is required';
        }
        if (!formData?.recentPhoto2) {
          newErrors.recentPhoto2 = 'Second recent photograph is required';
        }
        break;

      case 6: // Review
        if (!termsAccepted) {
          newErrors.terms = 'You must accept the terms and conditions';
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      if (currentStep === steps?.length - 1) {
        // On final step, trigger submission
        handleSubmit();
      } else {
        setCurrentStep(prev => prev + 1);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    } else {
      // If validation fails, scroll to top to show errors
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => prev - 1);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSave = async () => {
    setIsSaving(true);
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    localStorage.setItem('registrationFormData', JSON.stringify(formData));
    localStorage.setItem('registrationFormStep', currentStep?.toString());
    setIsSaving(false);
  };

  const handleSubmit = async (finalData = formData) => {
    // Final validation before submission
    if (!termsAccepted) {
      setError('Please accept the terms and conditions to submit your application');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // ENHANCED: More informative error message
    if (!companyId) {
      setError('Company not selected. Please go back to the company selection page and choose a company before continuing.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Validate email before submission
    if (!finalData?.email) {
      setError('Email is required for registration');
      return;
    }

    // Validate all required document uploads
    const requiredDocuments = [
      'tertiaryGraduationCert',
      'birthCertificate', 
      'nyscAssignmentLetter',
      'meansOfId',
      'passportPhoto',
      'recentPhoto1',
      'recentPhoto2'
    ];

    const missingDocuments = requiredDocuments?.filter(doc => !finalData?.[doc]);
    if (missingDocuments?.length > 0) {
      setError(`Missing required documents: ${missingDocuments?.join(', ')}. Please go back and upload all required documents.`);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    try {
      setLoading(true);
      setError(''); // Clear previous errors
      
      const result = await submissionService?.create({
        companyId,
        ...finalData,
      });
      
      // Set application ID for success modal
      if (result?.id) {
        setApplicationId(result?.id);
      }
      
      // Clear saved form data from localStorage
      localStorage.removeItem('registrationFormData');
      localStorage.removeItem('registrationFormStep');
      // ADDED: Clear company ID on successful submission
      localStorage.removeItem('registrationCompanyId');
      
      // Show success modal
      setShowSuccessModal(true);
    } catch (err) {
      console.error('Submission error:', err);
      
      // Provide user-friendly error messages
      if (err?.message?.includes('already exists') || err?.message?.includes('already submitted') || err?.message?.includes('already been used')) {
        setError(err?.message + ' If you need to update your information, please contact support.');
      } else if (err?.message?.includes('already been registered')) {
        setError(err?.message + ' Please verify your NYSC call-up number or contact support if you believe this is an error.');
      } else if (err?.message?.includes('cannot be the same')) {
        setError('Phone number validation failed: ' + err?.message);
      } else if (err?.message?.includes('Account creation failed')) {
        setError('Unable to create your account. Please try again or contact support.');
      } else {
        setError(err?.message || 'Failed to submit registration. Please try again.');
      }
      
      // Scroll to top to show error
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setLoading(false);
    }
  };

  const handleSuccessClose = () => {
    setShowSuccessModal(false);
    navigate('/employee-profile-hub');
  };

  const CurrentStepComponent = steps?.[currentStep]?.component;

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      {error && (
        <div className="container mx-auto px-4 mb-4">
          <div className="bg-red-50 p-4 rounded-lg text-red-700">
            {error}
          </div>
        </div>
      )}
      {/* Header */}
      <motion.header
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5 }}
        className="relative z-10 bg-card/80 backdrop-blur-sm border-b border-border shadow-brand"
      >
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 cursor-pointer hover:opacity-80 transition-opacity">
              <div className="flex items-center justify-center">
                <AppImage 
                  src="/assets/images/NYSC_logo-1765293658432.png" 
                  alt="National Youth Service Corps (NYSC) official logo"
                  className="w-12 h-12 object-contain"
                />
              </div>
              <div>
                <h1 className="text-xl font-bold text-gray-900">CorperConnect</h1>
                <p className="text-xs text-gray-600">Employee Registration Portal</p>
              </div>
            </Link>
            <Button
              variant="ghost"
              size="sm"
              iconName="X"
              onClick={() => {
                // ADDED: Clear companyId when user exits
                localStorage.removeItem('registrationCompanyId');
                navigate('/company-selection-hub');
              }}
            >
              Exit
            </Button>
          </div>
        </div>
      </motion.header>
      {/* Progress Indicator */}
      <ProgressIndicator
        currentStep={currentStep}
        totalSteps={steps?.length}
        steps={steps}
      />
      {/* Form Content */}
      <main className="container mx-auto px-4 py-8 relative z-10">
        {/* Company Header */}
        {companyInfo && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="bg-white rounded-2xl shadow-lg p-6 mb-6 border-l-4 border-blue-500"
          >
            <div className="flex items-center gap-4">
              {companyInfo?.logoUrl && (
                <div className="w-16 h-16 flex-shrink-0">
                  <AppImage
                    src={companyInfo?.logoUrl}
                    alt={`${companyInfo?.name} company logo`}
                    className="w-full h-full object-contain rounded-lg"
                  />
                </div>
              )}
              <div className="flex-1">
                <h2 className="text-2xl font-bold text-gray-900 mb-1">
                  {companyInfo?.name}
                </h2>
                <p className="text-sm text-gray-600">
                  Complete your registration to join our team
                </p>
              </div>
              <div className="hidden md:flex items-center gap-2 px-4 py-2 bg-blue-50 rounded-lg">
                <Icon name="Building2" size={20} color="#3B82F6" />
                <span className="text-sm font-medium text-blue-700 capitalize">
                  {companyInfo?.type?.replace('_', ' ')}
                </span>
              </div>
            </div>
          </motion.div>
        )}

        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8">
          {currentStep === 6 ? (
            <ReviewSubmitStep
              formData={formData}
              termsAccepted={termsAccepted}
              onTermsChange={setTermsAccepted}
            />
          ) : (
            <CurrentStepComponent
              formData={formData}
              errors={errors}
              onChange={handleFieldChange}
            />
          )}
        </div>

        {/* Help Section */}
        <div className="mt-6 bg-white rounded-lg border border-gray-200 p-4">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
              <Icon name="HelpCircle" size={16} color="#3B82F6" />
            </div>
            <div>
              <h4 className="text-sm font-semibold text-gray-900 mb-1">Need Help?</h4>
              <p className="text-xs text-gray-600 mb-2">
                If you're having trouble completing this form, our support team is here to help.
              </p>
              <div className="flex gap-2">
                <Button variant="ghost" size="xs" iconName="Mail">
                  support@corperconnect.com
                </Button>
                <Button variant="ghost" size="xs" iconName="Phone">
                  08133454231
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
      {/* Form Navigation */}
      <FormNavigation
        currentStep={currentStep}
        totalSteps={steps?.length}
        onPrevious={handlePrevious}
        onNext={handleNext}
        onSave={handleSave}
        isValid={currentStep === 6 ? termsAccepted : true}
        isSaving={isSaving}
      />
      {/* Success Modal */}
      <SuccessModal
        isOpen={showSuccessModal}
        applicationId={applicationId}
        onClose={handleSuccessClose}
      />
      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white p-6 rounded-lg">
            <div className="text-xl">Submitting your registration...</div>
          </div>
        </div>
      )}
    </div>
  );
}